/**
 * Componente Principal da Aplicação
 *
 * Define a estrutura de rotas e o layout geral da aplicação.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useEffect, ReactNode, Suspense, lazy, ComponentType } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useTheme } from '@contexts/ThemeContext';

// Layouts
import { MainLayout } from '@components/layout/MainLayout';

// Website institucional (página inicial pública)
import { Website } from '@pages/Website';
import { SegmentLanding } from '@pages/Website/SegmentLanding';
import { BlogIndexPage, BlogArticlePage } from '@pages/Website/blog/BlogPages';
import { PrivacyPage } from '@pages/Website/legal/PrivacyPage';
import { Subscription } from '@pages/Subscription';

// Páginas de autenticação
import { Login } from '@pages/Auth/Login';
import { Register } from '@pages/Auth/Register';
import { ForgotPassword } from '@pages/Auth/ForgotPassword';
import { ResetPassword } from '@pages/Auth/ResetPassword';

// Bloqueio de páginas por módulo do plano
import { PlanGate } from '@components/shared/PlanGate';
import { RoleGate, FINANCE_ROLES, STATUS_ROLES, INBOX_ROLES } from '@components/shared/RoleGate';

// Componente de loading
import { Spinner } from '@components/ui/Spinner';

// Páginas do painel e das lojas: carregadas sob demanda (code splitting),
// para o site público não baixar o código do sistema inteiro
const Dashboard = lazyPage(() => import('@pages/Dashboard'), 'Dashboard');
const Orders = lazyPage(() => import('@pages/Orders'), 'Orders');
const OrderDetails = lazyPage(() => import('@pages/Orders/OrderDetails'), 'OrderDetails');
const Products = lazyPage(() => import('@pages/Products'), 'Products');
const Categories = lazyPage(() => import('@pages/Categories'), 'Categories');
const Customers = lazyPage(() => import('@pages/Customers'), 'Customers');
const Tables = lazyPage(() => import('@pages/Tables'), 'Tables');
const Delivery = lazyPage(() => import('@pages/Delivery'), 'Delivery');
const Payments = lazyPage(() => import('@pages/Payments'), 'Payments');
const WhatsApp = lazyPage(() => import('@pages/WhatsApp'), 'WhatsApp');
const Financial = lazyPage(() => import('@pages/Financial'), 'Financial');
const Reports = lazyPage(() => import('@pages/Reports'), 'Reports');
const Settings = lazyPage(() => import('@pages/Settings'), 'Settings');
const LGPD = lazyPage(() => import('@pages/LGPD'), 'LGPD');
const Inventory = lazyPage(() => import('@pages/Inventory'), 'Inventory');
const Fiscal = lazyPage(() => import('@pages/Fiscal'), 'Fiscal');
const Marketing = lazyPage(() => import('@pages/Marketing'), 'Marketing');
const StorefrontAdmin = lazyPage(() => import('@pages/StorefrontAdmin'), 'StorefrontAdmin');
const PublicStore = lazyPage(() => import('@pages/PublicStore'), 'PublicStore');
const TableStore = lazyPage(() => import('@pages/TableStore'), 'TableStore');
const Audit = lazyPage(() => import('@pages/Audit'), 'Audit');
const UsersPage = lazyPage(() => import('@pages/Users'), 'UsersPage');
const Notifications = lazyPage(() => import('@pages/Notifications'), 'Notifications');
const Tenants = lazyPage(() => import('@pages/Tenants'), 'Tenants');
const Uploads = lazyPage(() => import('@pages/Uploads'), 'Uploads');
const Admin = lazyPage(() => import('@pages/Admin'), 'Admin');
const OrderQueuePanel = lazyPage(() => import('@pages/OrderQueuePanel'), 'OrderQueuePanel');
const Scheduling = lazyPage(() => import('@pages/Scheduling'), 'Scheduling');
const Customization = lazyPage(() => import('@pages/Customization'), 'Customization');
const HRPage = lazyPage(() => import('@pages/HR'), 'HRPage');
const Suppliers = lazyPage(() => import('@pages/Suppliers'), 'Suppliers');
const CostPlans = lazyPage(() => import('@pages/CostPlans'), 'CostPlans');
const CostUnits = lazyPage(() => import('@pages/CostUnits'), 'CostUnits');
const Expenses = lazyPage(() => import('@pages/Expenses'), 'Expenses');
const BankAccounts = lazyPage(() => import('@pages/BankAccounts'), 'BankAccounts');
const BankTransfers = lazyPage(() => import('@pages/BankTransfers'), 'BankTransfers');
const OFXReconciliation = lazyPage(() => import('@pages/OFXReconciliation'), 'OFXReconciliation');
const FinancialIncome = lazyPage(() => import('@pages/FinancialIncome'), 'FinancialIncome');
const FinancialReports = lazyPage(() => import('@pages/FinancialReports'), 'FinancialReports');
// CustomerPortal is now integrated into PublicStore as a drawer
const PixConfig = lazyPage(() => import('@pages/PixConfig'), 'PixConfig');
const Leads = lazyPage(() => import('@pages/Leads'), 'Leads');
const WhatsAppStatusPage = lazyPage(() => import('@pages/WhatsAppStatus'), 'WhatsAppStatus');
const WhatsAppInboxPage = lazyPage(() => import('@pages/WhatsAppInbox'), 'WhatsAppInbox');
const AbandonedCarts = lazyPage(() => import('@pages/AbandonedCarts'), 'AbandonedCarts');
const DeliveryAppPage = lazyPage(() => import('@pages/DeliveryApp'), 'DeliveryAppPage');

const CHUNK_RELOAD_KEY = 'vendeai:chunk-reload';

/** sessionStorage pode lançar erro (modo privado, cookies bloqueados) */
function safeSession<T>(fn: (s: Storage) => T): T | undefined {
  try {
    return fn(window.sessionStorage);
  } catch {
    return undefined;
  }
}

/**
 * React.lazy para módulos com export nomeado.
 * Se o arquivo do chunk não existir mais (deploy novo com a aba aberta),
 * recarrega a página uma vez para buscar a versão atual.
 */
function lazyPage<K extends string>(loader: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(() =>
    loader().then(
      m => {
        safeSession(s => s.removeItem(CHUNK_RELOAD_KEY));
        return { default: m[name] };
      },
      err => {
        // null = storage disponível e ainda sem recarga; undefined = storage
        // indisponível (não recarrega, para não entrar em loop)
        if (safeSession(s => s.getItem(CHUNK_RELOAD_KEY)) === null) {
          safeSession(s => s.setItem(CHUNK_RELOAD_KEY, '1'));
          window.location.reload();
          return new Promise<never>(() => {});
        }
        throw err;
      },
    ),
  );
}

/** Carregamento exibido enquanto o código da página é baixado */
function PageLoading() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

/** Redireciona /loja/:slug/portal para /loja/:slug (portal agora é drawer) */
function PortalRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/loja/${slug}`} replace />;
}

/**
 * Wrapper que força modo claro nas vitrines públicas.
 * Quando o admin está em dark mode, as vitrines devem permanecer em light mode
 * pois usam cores customizadas do tenant.
 */
function ForceLightMode({ children }: { children: ReactNode }) {
  const { forceLight } = useTheme();

  // Registra o bloqueio no ThemeProvider (que aplica as classes no documento).
  // Manipular as classes aqui não funciona no carregamento direto da página,
  // pois o efeito do provider (pai) roda depois e reaplica o tema salvo.
  useEffect(() => forceLight(), [forceLight]);

  return <>{children}</>;
}

/**
 * Componente de rota privada
 * Redireciona para login se não autenticado
 */
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  // Exibe loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redireciona para login se não autenticado
  if (!isAuthenticated) {
    return <Navigate to="/entrar" replace />;
  }

  return <>{children}</>;
}

/**
 * Componente de rota pública
 * Redireciona para dashboard se já autenticado
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  // Exibe loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redireciona para painel se já autenticado
  if (isAuthenticated) {
    return <Navigate to="/painel" replace />;
  }

  return <>{children}</>;
}

/**
 * Rotas da aplicação
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Website institucional: página inicial pública (vitrine de vendas do Vende Aí).
          Usa paleta própria; ForceLightMode evita que variantes "dark:" globais do painel vazem. */}
      <Route path="/" element={<ForceLightMode><Website /></ForceLightMode>} />
      {/* Páginas do site por segmento (pizzaria, hamburgueria, barbearia...) */}
      <Route path="/para/:segmento" element={<ForceLightMode><SegmentLanding /></ForceLightMode>} />
      {/* Blog do site (guias para donos de negócio) */}
      <Route
        path="/blog"
        element={
          <ForceLightMode>
            <BlogIndexPage />
          </ForceLightMode>
        }
      />
      <Route
        path="/blog/:slug"
        element={
          <ForceLightMode>
            <BlogArticlePage />
          </ForceLightMode>
        }
      />
      <Route
        path="/privacidade"
        element={
          <ForceLightMode>
            <PrivacyPage />
          </ForceLightMode>
        }
      />

      {/* Rotas públicas (login, registro, etc.) */}
      <Route
        path="/entrar"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/cadastro"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/esqueci-senha"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/redefinir-senha"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      {/* Rotas protegidas (dentro do layout principal) */}
      <Route
        element={
          <PrivateRoute>
            <MainLayout />
          </PrivateRoute>
        }
      >
        {/* Painel */}
        <Route path="painel" element={<Dashboard />} />

        {/* Pedidos */}
        <Route path="pedidos" element={<Orders />} />
        <Route path="pedidos/:id" element={<OrderDetails />} />

        {/* Produtos */}
        <Route path="produtos" element={<Products />} />

        {/* Categorias */}
        <Route path="categorias" element={<Categories />} />

        {/* Personalizacao (Tamanhos e Complementos) */}
        <Route path="personalizacao" element={<Customization />} />

        {/* Clientes */}
        <Route path="clientes" element={<Customers />} />

        {/* Mesas */}
        <Route path="mesas" element={<PlanGate module="tables"><Tables /></PlanGate>} />

        {/* Entregas */}
        <Route path="entregas" element={<Delivery />} />
        <Route path="entregas-app" element={<RoleGate roles={STATUS_ROLES} message="As entregas por aplicativo são configuradas por Gerente e Proprietário."><DeliveryAppPage /></RoleGate>} />

        {/* Pagamentos */}
        <Route path="pagamentos" element={<Payments />} />

        {/* WhatsApp */}
        <Route path="whatsapp" element={<RoleGate roles={INBOX_ROLES} message="O WhatsApp é liberado para Atendente, Gerente e Proprietário."><PlanGate module="whatsapp"><WhatsApp /></PlanGate></RoleGate>} />

        {/* Status do WhatsApp com programação (#242): Proprietário, Gerente e super admin */}
        <Route path="status-whatsapp" element={<RoleGate roles={STATUS_ROLES}><PlanGate module="whatsapp"><WhatsAppStatusPage /></PlanGate></RoleGate>} />

        {/* Central de atendimento do WhatsApp (#252): Atendente, Gerente, Proprietário e super admin */}
        <Route path="atendimento" element={<RoleGate roles={INBOX_ROLES} message="A Central de atendimento é liberada para Atendente, Gerente e Proprietário."><PlanGate module="whatsapp"><WhatsAppInboxPage /></PlanGate></RoleGate>} />

        {/* Leads WhatsApp */}
        <Route path="leads" element={<RoleGate roles={INBOX_ROLES} message="Os leads do WhatsApp são liberados para Atendente, Gerente e Proprietário."><PlanGate module="whatsapp"><Leads /></PlanGate></RoleGate>} />

        {/* Carrinhos Abandonados */}
        <Route path="carrinhos-abandonados" element={<RoleGate roles={INBOX_ROLES} message="Os carrinhos abandonados são liberados para Atendente, Gerente e Proprietário."><PlanGate module="abandonedCarts"><AbandonedCarts /></PlanGate></RoleGate>} />

        {/* Financeiro */}
        <Route path="financeiro" element={<RoleGate roles={FINANCE_ROLES}><PlanGate module="financial"><Financial /></PlanGate></RoleGate>} />

        {/* Despesas */}
        <Route path="despesas" element={<RoleGate roles={FINANCE_ROLES}><PlanGate module="financial"><Expenses /></PlanGate></RoleGate>} />

        {/* Fornecedores */}
        <Route path="fornecedores" element={<PlanGate module="inventory"><Suppliers /></PlanGate>} />

        {/* Plano de Custos */}
        <Route path="plano-custos" element={<RoleGate roles={FINANCE_ROLES}><PlanGate module="financial"><CostPlans /></PlanGate></RoleGate>} />

        {/* Unidade de Custos */}
        <Route path="unidade-custos" element={<RoleGate roles={FINANCE_ROLES}><PlanGate module="financial"><CostUnits /></PlanGate></RoleGate>} />

        {/* Contas Bancárias */}
        <Route path="contas-bancarias" element={<RoleGate roles={FINANCE_ROLES}><PlanGate module="financial"><BankAccounts /></PlanGate></RoleGate>} />

        {/* Transferências Bancárias */}
        <Route path="transferencias" element={<RoleGate roles={FINANCE_ROLES}><PlanGate module="financial"><BankTransfers /></PlanGate></RoleGate>} />

        {/* Conciliação Bancária (OFX) */}
        <Route path="conciliacao-bancaria" element={<RoleGate roles={FINANCE_ROLES}><PlanGate module="bankReconciliation"><OFXReconciliation /></PlanGate></RoleGate>} />

        {/* Receitas Financeiras */}
        <Route path="receitas" element={<RoleGate roles={FINANCE_ROLES}><PlanGate module="financial"><FinancialIncome /></PlanGate></RoleGate>} />

        {/* Relatórios Financeiros e KPIs */}
        <Route path="relatorios-financeiros" element={<RoleGate roles={FINANCE_ROLES}><PlanGate module="financial"><FinancialReports /></PlanGate></RoleGate>} />

        {/* Relatórios */}
        <Route path="relatorios" element={<Reports />} />

        {/* Configurações */}
        <Route path="configuracoes" element={<Settings />} />
        <Route path="configuracoes/pix" element={<PixConfig />} />

        {/* LGPD - Proteção de Dados */}
        <Route path="lgpd" element={<LGPD />} />

        {/* Estoque (unificado: produtos + ingredientes) */}
        <Route path="estoque" element={<PlanGate module="inventory"><Inventory /></PlanGate>} />

        {/* Fiscal / NF-e */}
        <Route path="fiscal" element={<PlanGate module="fiscal"><Fiscal /></PlanGate>} />

        {/* Marketing / Cupons */}
        <Route path="marketing" element={<Marketing />} />

        {/* Vitrine / Loja Virtual (gerenciamento) */}
        <Route path="vitrine" element={<StorefrontAdmin />} />

        {/* Recursos Humanos */}
        <Route path="rh" element={<PlanGate module="hr"><HRPage /></PlanGate>} />

        {/* Auditoria */}
        <Route path="auditoria" element={<Audit />} />

        {/* Usuários */}
        <Route path="usuarios" element={<UsersPage />} />

        {/* Notificações */}
        <Route path="notificacoes" element={<Notifications />} />

        {/* Agendamentos */}
        <Route path="agendamentos" element={<Scheduling />} />

        {/* Meu Estabelecimento */}
        <Route path="estabelecimento" element={<Tenants />} />

        {/* Galeria de Mídias */}
        <Route path="midias" element={<Uploads />} />

        {/* Painel Super Admin */}
        <Route path="admin" element={<Admin />} />
      </Route>

      {/* Assinatura do estabelecimento: fora do layout para funcionar com acesso expirado */}
      <Route
        path="/assinatura"
        element={
          <PrivateRoute>
            <Subscription />
          </PrivateRoute>
        }
      />

      {/* Painel de fila de pedidos (fullscreen para TV) */}
      <Route
        path="/fila-pedidos"
        element={
          <PrivateRoute>
            <OrderQueuePanel />
          </PrivateRoute>
        }
      />

      {/* Vitrine pública (acessada pelo slug do tenant) - sempre light mode */}
      <Route path="/loja/:slug" element={<ForceLightMode><PublicStore /></ForceLightMode>} />

      {/* Portal do cliente agora é um drawer integrado no PublicStore - redireciona URLs antigas */}
      <Route path="/loja/:slug/portal" element={<PortalRedirect />} />

      {/* Cardápio de mesa via QR Code (público, sem autenticação) - sempre light mode */}
      <Route path="/mesa/:slug/:tableNumber" element={<ForceLightMode><TableStore /></ForceLightMode>} />

      {/* Rota 404 */}
      <Route
        path="*"
        element={
          <div className="flex h-screen flex-col items-center justify-center">
            <h1 className="text-4xl font-bold text-primary-800 dark:text-white">
              404
            </h1>
            <p className="mt-2 text-primary-600 dark:text-primary-400">
              Página não encontrada
            </p>
          </div>
        }
      />
    </Routes>
  );
}

/**
 * Componente App
 * Define todas as rotas da aplicação. As páginas do painel e das lojas são
 * carregadas sob demanda; o Suspense mostra o carregamento enquanto isso.
 */
function App() {
  return (
    <Suspense fallback={<PageLoading />}>
      <AppRoutes />
    </Suspense>
  );
}

export default App;
